import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card } from 'primereact/card';
import { Tree } from 'primereact/tree';
import { TreeNode } from 'primereact/treenode';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { InputNumber } from 'primereact/inputnumber';
import { Dropdown } from 'primereact/dropdown';
import { Toast } from 'primereact/toast';
import { Message } from 'primereact/message';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Badge } from 'primereact/badge';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import {
  LogisticsCategory,
  LogisticsGood,
  LogisticsGoodSupplier,
  LogisticsGoodPriceHistory
} from '../types';
import { logisticsCategoriesAPI, logisticsGoodsAPI } from '../services/api';

const monthLabels = ['Jan', 'Feb', 'Mar', 'Apr', 'Maj', 'Jun', 'Jul', 'Avg', 'Sep', 'Okt', 'Nov', 'Dec'];

const formatMonth = (month: number, year: number) => `${monthLabels[month - 1]} ${year}.`;

const formatDateShort = (iso?: string) => {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleDateString('sr-RS');
  } catch {
    return iso;
  }
};

const formatMonthDate = (month: number, year: number) => {
  const d = new Date(year, month - 1, 1);
  const dd = String(d.getDate()).padStart(2, '0');
  return `${dd}. ${monthLabels[month - 1]} ${year}.`;
};

const currencyOptions = [
  { label: 'RSD', value: 'RSD' },
  { label: 'EUR', value: 'EUR' },
  { label: 'USD', value: 'USD' }
];

const unitOptions = [
  { label: 'kom', value: 'kom' },
  { label: 'kg', value: 'kg' },
  { label: 'l', value: 'l' },
  { label: 'm', value: 'm' },
  { label: 'set', value: 'set' }
];

const pipelineOptions = [
  { label: 'Lead', value: 'lead' },
  { label: 'Info razgovor', value: 'info_razgovor' },
  { label: 'Pregovori', value: 'pregovori' }
];

const LogisticsGoods: React.FC = () => {
  const toast = useRef<Toast>(null);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [categories, setCategories] = useState<LogisticsCategory[]>([]);
  const [goods, setGoods] = useState<LogisticsGood[]>([]);

  const [treeFilter, setTreeFilter] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [selectedGoodId, setSelectedGoodId] = useState<string | null>(null);

  const [search, setSearch] = useState('');

  // Good dialog
  const [goodDialogVisible, setGoodDialogVisible] = useState(false);
  const [editingGood, setEditingGood] = useState<LogisticsGood | null>(null);
  const [goodForm, setGoodForm] = useState({
    name: '',
    code: '',
    description: '',
    unit: 'kom',
    categoryId: ''
  });
  const [goodSuppliers, setGoodSuppliers] = useState<LogisticsGoodSupplier[]>([]);

  // Supplier dialog
  const [supplierDialogVisible, setSupplierDialogVisible] = useState(false);
  const [editingSupplierIndex, setEditingSupplierIndex] = useState<number | null>(null);
  const [supplierForm, setSupplierForm] = useState<LogisticsGoodSupplier>({
    name: '',
    contactPerson: '',
    phone: '',
    email: '',
    notes: '',
    currency: 'RSD',
    pipelineStage: 'lead',
    priceHistory: [],
    contactHistory: [],
    pipelineHistory: []
  });
  const [supplierPrices, setSupplierPrices] = useState<Record<string, number | null>>({});

  const categoryOptions = useMemo(
    () => categories.map((c) => ({ label: c.name, value: c._id })),
    [categories]
  );

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const [cats, gds] = await Promise.all([logisticsCategoriesAPI.getAll(), logisticsGoodsAPI.getAll()]);
      setCategories(cats);
      setGoods(gds);
    } catch (e: any) {
      console.error(e);
      setError(e?.response?.data?.message || 'Greška pri učitavanju kataloga');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const getCategoryName = (id: string) => categories.find((c) => c._id === id)?.name || '';

  const selectedCategoryLabel = useMemo(() => (selectedCategoryId ? getCategoryName(selectedCategoryId) : ''), [selectedCategoryId, categories]);
  const selectedGoodLabel = useMemo(() => {
    const g = selectedGoodId ? goods.find((x) => x._id === selectedGoodId) : null;
    return g ? g.name : '';
  }, [selectedGoodId, goods]);

  const normalizedGoods = useMemo(() => {
    // apply category selection + search
    const q = search.trim().toLowerCase();
    return goods.filter((g) => {
      const catId = typeof g.category === 'string' ? g.category : g.category?._id;
      if (selectedCategoryId && catId !== selectedCategoryId) return false;
      if (!q) return true;
      const name = (g.name || '').toLowerCase();
      const code = (g.code || '').toLowerCase();
      const suppliers = (g.suppliers || []).map((s) => (s.name || '').toLowerCase()).join(' ');
      return name.includes(q) || code.includes(q) || suppliers.includes(q);
    });
  }, [goods, selectedCategoryId, search]);

  const getLastPriceEntry = (g: LogisticsGood): LogisticsGoodPriceHistory & { supplierName: string } | null => {
    const all: Array<LogisticsGoodPriceHistory & { supplierName: string }> = [];
    (g.suppliers || []).forEach((s) => {
      (s.priceHistory || []).forEach((p) => all.push({ ...p, supplierName: s.name }));
    });
    if (!all.length) return null;
    all.sort((a, b) => (a.year === b.year ? b.month - a.month : b.year - a.year));
    return all[0];
  };

  const getLowestPriceEntry = (g: LogisticsGood): (LogisticsGoodPriceHistory & { supplierName: string }) | null => {
    const all: Array<LogisticsGoodPriceHistory & { supplierName: string }> = [];
    (g.suppliers || []).forEach((s) => {
      (s.priceHistory || []).forEach((p) => all.push({ ...p, supplierName: s.name }));
    });
    if (!all.length) return null;
    all.sort((a, b) => a.price - b.price);
    return all[0];
  };

  const suppliersCountBody = (g: LogisticsGood) => <Badge value={g.suppliers?.length || 0} />;

  const lastOfferBody = (g: LogisticsGood) => {
    const last = getLastPriceEntry(g);
    if (!last) return <span className="text-600">-</span>;
    return (
      <span>
        {last.price} {last.currency} <span className="text-600">({formatMonth(last.month, last.year)})</span>
      </span>
    );
  };

  const lowestOfferBody = (g: LogisticsGood) => {
    const low = getLowestPriceEntry(g);
    if (!low) return <span className="text-600">Nema podataka o ceni</span>;
    return (
      <span>
        {Number(low.price).toFixed(2)} {low.currency}
      </span>
    );
  };

  const lowestSupplierBody = (g: LogisticsGood) => {
    const low = getLowestPriceEntry(g);
    return low ? low.supplierName : <span className="text-600">-</span>;
  };

  const lowestDateBody = (g: LogisticsGood) => {
    const low = getLowestPriceEntry(g);
    return low ? formatMonthDate(low.month, low.year) : <span className="text-600">-</span>;
  };

  const openGoodDialog = (g?: LogisticsGood) => {
    if (g) {
      setEditingGood(g);
      const catId = typeof g.category === 'string' ? g.category : g.category?._id;
      setGoodForm({
        name: g.name || '',
        code: g.code || '',
        description: g.description || '',
        unit: g.unit || 'kom',
        categoryId: catId || ''
      });
      setGoodSuppliers(g.suppliers || []);
    } else {
      setEditingGood(null);
      setGoodForm({ name: '', code: '', description: '', unit: 'kom', categoryId: selectedCategoryId || '' });
      setGoodSuppliers([]);
    }
    setGoodDialogVisible(true);
  };

  const saveGood = async () => {
    const name = goodForm.name.trim();
    if (!name) {
      toast.current?.show({ severity: 'warn', summary: 'Validacija', detail: 'Naziv robe je obavezan', life: 2500 });
      return;
    }
    if (!goodForm.categoryId) {
      toast.current?.show({ severity: 'warn', summary: 'Validacija', detail: 'Kategorija je obavezna', life: 2500 });
      return;
    }
    setLoading(true);
    try {
      if (editingGood) {
        await logisticsGoodsAPI.update(editingGood._id, {
          name,
          code: goodForm.code?.trim() || '',
          description: goodForm.description || '',
          unit: goodForm.unit || 'kom',
          categoryId: goodForm.categoryId,
          suppliers: goodSuppliers
        });
        toast.current?.show({ severity: 'success', summary: 'Uspešno', detail: 'Roba je ažurirana', life: 2500 });
      } else {
        await logisticsGoodsAPI.create({
          name,
          code: goodForm.code?.trim() || '',
          description: goodForm.description || '',
          unit: goodForm.unit || 'kom',
          categoryId: goodForm.categoryId,
          suppliers: goodSuppliers
        });
        toast.current?.show({ severity: 'success', summary: 'Uspešno', detail: 'Roba je kreirana', life: 2500 });
      }
      setGoodDialogVisible(false);
      await load();
    } catch (e: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Greška',
        detail: e?.response?.data?.message || 'Greška pri čuvanju robe',
        life: 5000
      });
    } finally {
      setLoading(false);
    }
  };

  const deleteGood = (g: LogisticsGood) => {
    confirmDialog({
      header: 'Potvrda brisanja',
      message: `Obrisati robu "${g.name}"?`,
      icon: 'pi pi-exclamation-triangle',
      accept: async () => {
        try {
          await logisticsGoodsAPI.delete(g._id);
          toast.current?.show({ severity: 'success', summary: 'Uspešno', detail: 'Roba obrisana', life: 2500 });
          await load();
        } catch (e: any) {
          toast.current?.show({
            severity: 'error',
            summary: 'Greška',
            detail: e?.response?.data?.message || 'Greška pri brisanju',
            life: 5000
          });
        }
      }
    });
  };

  const openSupplierDialog = (idx?: number) => {
    setEditingSupplierIndex(idx ?? null);
    if (idx !== undefined && idx !== null) {
      const s = goodSuppliers[idx];
      const copy: LogisticsGoodSupplier = {
        ...s,
        currency: (s.currency as any) || 'RSD',
        pipelineStage: s.pipelineStage || 'lead',
        priceHistory: s.priceHistory || [],
        contactHistory: s.contactHistory || [],
        pipelineHistory: s.pipelineHistory || []
      };
      setSupplierForm(copy);
      const map: Record<string, number | null> = {};
      (copy.priceHistory || []).forEach((p) => {
        map[`${p.year}-${String(p.month).padStart(2, '0')}`] = p.price;
      });
      setSupplierPrices(map);
    } else {
      setSupplierForm({
        name: '',
        contactPerson: '',
        phone: '',
        email: '',
        notes: '',
        currency: 'RSD',
        pipelineStage: 'lead',
        priceHistory: [],
        contactHistory: [],
        pipelineHistory: []
      });
      setSupplierPrices({});
    }
    setSupplierDialogVisible(true);
  };

  const last12Months = useMemo(() => {
    const out: Array<{ key: string; year: number; month: number; label: string }> = [];
    const now = dayjs();
    for (let i = 0; i < 12; i++) {
      const d = now.subtract(i, 'month');
      const year = d.year();
      const month = d.month() + 1;
      const key = `${year}-${String(month).padStart(2, '0')}`;
      out.push({ key, year, month, label: `${monthLabels[month - 1]} ${year}.` });
    }
    return out.reverse();
  }, []);

  const saveSupplier = () => {
    const name = (supplierForm.name || '').trim();
    if (!name) {
      toast.current?.show({ severity: 'warn', summary: 'Validacija', detail: 'Naziv dobavljača je obavezan', life: 2500 });
      return;
    }
    const currency = (supplierForm.currency as any) || 'RSD';
    const priceHistory: LogisticsGoodPriceHistory[] = [];
    last12Months.forEach((m) => {
      const v = supplierPrices[m.key];
      if (v === null || v === undefined || v === ('' as any)) return;
      if (Number.isNaN(Number(v))) return;
      if (Number(v) < 0) return;
      priceHistory.push({ month: m.month, year: m.year, price: Number(v), currency });
    });

    const sanitized: LogisticsGoodSupplier = {
      name,
      contactPerson: supplierForm.contactPerson?.trim() || '',
      phone: supplierForm.phone?.trim() || '',
      email: supplierForm.email?.trim() || '',
      notes: supplierForm.notes || '',
      currency,
      pipelineStage: supplierForm.pipelineStage || 'lead',
      lastContactedAt: supplierForm.lastContactedAt || undefined,
      priceHistory,
      contactHistory: supplierForm.contactHistory || [],
      pipelineHistory: supplierForm.pipelineHistory || []
    };

    setGoodSuppliers((prev) => {
      const next = [...prev];
      if (editingSupplierIndex === null) next.push(sanitized);
      else next[editingSupplierIndex] = sanitized;
      return next;
    });
    setSupplierDialogVisible(false);
  };

  const deleteSupplier = (idx: number) => {
    setGoodSuppliers((prev) => prev.filter((_, i) => i !== idx));
  };

  const actionsBody = (g: LogisticsGood) => (
    <div className="flex gap-2 justify-content-end">
      <Button
        icon="pi pi-folder-open"
        className="p-button-sm p-button-text"
        onClick={() => openGoodDialog(g)}
        tooltip="Otvori folder"
        tooltipOptions={{ position: 'top' }}
      />
      <Button icon="pi pi-pencil" className="p-button-sm p-button-text" onClick={() => openGoodDialog(g)} />
      <Button icon="pi pi-trash" className="p-button-sm p-button-text p-button-danger" onClick={() => deleteGood(g)} />
    </div>
  );

  const buildTreeNodes = (): TreeNode[] => {
    const byParent = new Map<string | null, LogisticsCategory[]>();
    categories.forEach((c) => {
      const p = (c.parentCategory as any) || null;
      byParent.set(p, [...(byParent.get(p) || []), c]);
    });
    // sort
    byParent.forEach((arr, key) => byParent.set(key, arr.slice().sort((a, b) => a.name.localeCompare(b.name))));

    const goodsByCategory = new Map<string, LogisticsGood[]>();
    goods.forEach((g) => {
      const catId = typeof g.category === 'string' ? g.category : g.category?._id;
      if (!catId) return;
      goodsByCategory.set(catId, [...(goodsByCategory.get(catId) || []), g]);
    });
    goodsByCategory.forEach((arr, key) => goodsByCategory.set(key, arr.slice().sort((a, b) => a.name.localeCompare(b.name))));

    const buildCategoryNode = (c: LogisticsCategory): TreeNode => {
      const childrenCats = (byParent.get(c._id) || []).map(buildCategoryNode);
      const childrenGoods = (goodsByCategory.get(c._id) || []).map((g) => ({
        key: `good:${g._id}`,
        label: g.name,
        icon: 'pi pi-box',
        data: { type: 'good', id: g._id, categoryId: c._id },
        children: (g.suppliers || []).map((s) => ({
          key: `supplier:${g._id}:${s.name}`,
          label: s.name,
          icon: 'pi pi-user',
          selectable: false
        }))
      })) as TreeNode[];
      return {
        key: `cat:${c._id}`,
        label: c.name,
        icon: 'pi pi-folder',
        data: { type: 'category', id: c._id },
        children: [...childrenCats, ...childrenGoods]
      };
    };

    return (byParent.get(null) || []).map(buildCategoryNode);
  };

  const treeNodes = useMemo(() => buildTreeNodes(), [categories, goods]);

  const onTreeSelect = (e: any) => {
    const node = e.node as TreeNode;
    const data: any = node.data;
    if (!data) return;
    if (data.type === 'category') {
      setSelectedCategoryId(data.id);
      setSelectedGoodId(null);
    }
    if (data.type === 'good') {
      setSelectedCategoryId(data.categoryId || null);
      setSelectedGoodId(data.id);
      const g = goods.find((x) => x._id === data.id);
      if (g) openGoodDialog(g);
    }
  };

  return (
    <div className="p-4">
      <Toast ref={toast} />
      <ConfirmDialog />

      <div className="flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <div>
          <h2 className="m-0">Roba</h2>
          <div className="text-600 mt-1">Pratite dobavljače i ponude za poslednjih 12 meseci</div>
        </div>
      </div>

      {error && <Message severity="error" text={error} className="mb-3" />}

      <div className="grid">
        <div className="col-12">
          <Card title="Struktura kataloga" subTitle="Kategorija → roba → dobavljači">
            <div className="mb-3">
              <span className="p-input-icon-right w-full">
                <i className="pi pi-search" />
                <InputText value={treeFilter} onChange={(e) => setTreeFilter(e.target.value)} placeholder="Pretraži" className="w-full" />
              </span>
            </div>
            <Tree value={treeNodes} filter filterValue={treeFilter} filterMode="lenient" selectionMode="single" onSelect={onTreeSelect} />
            <div className="mt-3 text-700" style={{ fontSize: 13 }}>
              {selectedCategoryLabel && (
                <div>
                  Izabrana kategorija: <strong>{selectedCategoryLabel}</strong>
                </div>
              )}
              {selectedGoodLabel && (
                <div>
                  Izabrana roba: <strong>{selectedGoodLabel}</strong>
                </div>
              )}
            </div>
          </Card>
        </div>

        <div className="col-12">
          <Card>
            <div className="flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
              <div className="flex gap-2">
                <Button label="Dodaj robu" icon="pi pi-plus" severity="info" onClick={() => openGoodDialog()} />
                <Button label="Dodaj kategoriju" icon="pi pi-folder" severity="secondary" onClick={() => navigate('/logistics/categories')} />
                <Button label="Osveži" icon="pi pi-refresh" severity="secondary" onClick={load} />
              </div>
              <span className="p-input-icon-right" style={{ minWidth: 320 }}>
                <i className="pi pi-search" />
                <InputText value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Pretraga po nazivu, šifri ili dobavljaču..." className="w-full" />
              </span>
            </div>

            <DataTable value={normalizedGoods} loading={loading} paginator rows={10} emptyMessage="Nema robe" className="p-datatable-sm" responsiveLayout="stack" breakpoint="768px">
              <Column field="name" header="Naziv" sortable />
              <Column header="Interni kod" body={(g: LogisticsGood) => g.code || '-'} style={{ width: '130px' }} />
              <Column header="Kategorija" body={(g: LogisticsGood) => (typeof g.category === 'string' ? getCategoryName(g.category) : g.category?.name)} sortable />
              <Column header="Dobavljači" body={suppliersCountBody} style={{ width: '110px' }} />
              <Column header="Poslednja ponuda" body={lastOfferBody} />
              <Column header="Poslednja najniža ponuda" body={lowestOfferBody} />
              <Column header="Dobavljač" body={lowestSupplierBody} style={{ width: '160px' }} />
              <Column header="Datum" body={lowestDateBody} style={{ width: '160px' }} />
              <Column header="Akcije" body={actionsBody} style={{ width: '150px' }} />
            </DataTable>
          </Card>
        </div>
      </div>

      <Dialog
        header={editingGood ? `Pregled foldera - ${editingGood.name}` : 'Dodaj robu'}
        visible={goodDialogVisible}
        onHide={() => setGoodDialogVisible(false)}
        style={{ width: '70vw', maxWidth: 1100 }}
        breakpoints={{ '1200px': '85vw', '960px': '95vw', '640px': '98vw' }}
        contentStyle={{ maxHeight: '70vh', overflow: 'auto' }}
        draggable={false}
        modal
        footer={
          <div className="flex justify-content-end gap-2">
            <Button label="Otkaži" className="p-button-secondary p-button-outlined" onClick={() => setGoodDialogVisible(false)} />
            <Button label="Sačuvaj" icon="pi pi-check" severity="info" onClick={saveGood} loading={loading} />
          </div>
        }
      >
        <div className="p-fluid">
          <div className="grid">
            <div className="col-12 md:col-6">
              <div className="field">
                <label>Naziv *</label>
                <InputText value={goodForm.name} onChange={(e) => setGoodForm((p) => ({ ...p, name: e.target.value }))} />
              </div>
            </div>
            <div className="col-12 md:col-6">
              <div className="field">
                <label>Interni kod</label>
                <InputText value={goodForm.code} onChange={(e) => setGoodForm((p) => ({ ...p, code: e.target.value }))} />
              </div>
            </div>
            <div className="col-12 md:col-6">
              <div className="field">
                <label>Kategorija *</label>
                <Dropdown
                  value={goodForm.categoryId}
                  options={categoryOptions}
                  onChange={(e) => setGoodForm((p) => ({ ...p, categoryId: e.value }))}
                  appendTo="self"
                  filter
                  showClear
                  placeholder="Izaberite kategoriju"
                />
              </div>
            </div>
            <div className="col-12 md:col-6">
              <div className="field">
                <label>Jedinica</label>
                <Dropdown value={goodForm.unit} options={unitOptions} onChange={(e) => setGoodForm((p) => ({ ...p, unit: e.value }))} appendTo="self" />
              </div>
            </div>
            <div className="col-12">
              <div className="field">
                <label>Opis</label>
                <InputTextarea value={goodForm.description} onChange={(e) => setGoodForm((p) => ({ ...p, description: e.target.value }))} rows={4} />
              </div>
            </div>
          </div>

          <div className="flex justify-content-between align-items-center mt-3 mb-2">
            <div style={{ fontWeight: 700 }}>Dobavljači</div>
            <Button label="Dodaj dobavljača" icon="pi pi-user-plus" onClick={() => openSupplierDialog()} />
          </div>

          <DataTable value={goodSuppliers} emptyMessage="Nema dobavljača" className="p-datatable-sm" responsiveLayout="stack" breakpoint="768px">
            <Column field="name" header="Naziv dobavljača" />
            <Column field="contactPerson" header="Kontakt osoba" />
            <Column field="phone" header="Telefon" />
            <Column field="email" header="Email" />
            <Column header="Valuta" body={(s: LogisticsGoodSupplier) => s.currency || 'RSD'} style={{ width: '120px' }} />
            <Column header="Poslednji kontakt" body={(s: LogisticsGoodSupplier) => formatDateShort(s.lastContactedAt)} style={{ width: '160px' }} />
            <Column header="Pipeline status" body={(s: LogisticsGoodSupplier) => s.pipelineStage || 'lead'} style={{ width: '160px' }} />
            <Column
              header="Akcije"
              body={(_: LogisticsGoodSupplier, opts: any) => (
                <div className="flex gap-2 justify-content-end">
                  <Button icon="pi pi-pencil" className="p-button-sm p-button-text" onClick={() => openSupplierDialog(opts.rowIndex)} />
                  <Button icon="pi pi-trash" className="p-button-sm p-button-text p-button-danger" onClick={() => deleteSupplier(opts.rowIndex)} />
                </div>
              )}
              style={{ width: '140px' }}
            />
          </DataTable>
        </div>
      </Dialog>

      <Dialog
        header={editingSupplierIndex === null ? 'Dodaj dobavljača' : 'Izmeni dobavljača'}
        visible={supplierDialogVisible}
        onHide={() => setSupplierDialogVisible(false)}
        style={{ width: '70vw', maxWidth: 1000 }}
        breakpoints={{ '1200px': '85vw', '960px': '95vw', '640px': '98vw' }}
        contentStyle={{ maxHeight: '70vh', overflow: 'auto' }}
        draggable={false}
        modal
        footer={
          <div className="flex justify-content-end gap-2">
            <Button label="Otkaži" className="p-button-secondary p-button-outlined" onClick={() => setSupplierDialogVisible(false)} />
            <Button label="Sačuvaj" icon="pi pi-check" severity="info" onClick={saveSupplier} />
          </div>
        }
      >
        <div className="p-fluid">
          <div className="grid">
            <div className="col-12 md:col-6">
              <div className="field">
                <label>Naziv dobavljača *</label>
                <InputText value={supplierForm.name || ''} onChange={(e) => setSupplierForm((p) => ({ ...p, name: e.target.value }))} />
              </div>
            </div>
            <div className="col-12 md:col-6">
              <div className="field">
                <label>Kontakt osoba</label>
                <InputText value={supplierForm.contactPerson || ''} onChange={(e) => setSupplierForm((p) => ({ ...p, contactPerson: e.target.value }))} />
              </div>
            </div>
            <div className="col-12 md:col-6">
              <div className="field">
                <label>Telefon</label>
                <InputText value={supplierForm.phone || ''} onChange={(e) => setSupplierForm((p) => ({ ...p, phone: e.target.value }))} />
              </div>
            </div>
            <div className="col-12 md:col-6">
              <div className="field">
                <label>Email</label>
                <InputText value={supplierForm.email || ''} onChange={(e) => setSupplierForm((p) => ({ ...p, email: e.target.value }))} />
              </div>
            </div>
            <div className="col-12 md:col-4">
              <div className="field">
                <label>Valuta</label>
                <Dropdown
                  value={supplierForm.currency || 'RSD'}
                  options={currencyOptions}
                  onChange={(e) => setSupplierForm((p) => ({ ...p, currency: e.value }))}
                  appendTo="self"
                />
              </div>
            </div>
            <div className="col-12 md:col-4">
              <div className="field">
                <label>Pipeline status</label>
                <Dropdown
                  value={supplierForm.pipelineStage || 'lead'}
                  options={pipelineOptions}
                  onChange={(e) => setSupplierForm((p) => ({ ...p, pipelineStage: e.value }))}
                  appendTo="self"
                />
              </div>
            </div>
            <div className="col-12 md:col-4">
              <div className="field">
                <label>Poslednji kontakt (opciono)</label>
                <InputText
                  value={supplierForm.lastContactedAt ? formatDateShort(supplierForm.lastContactedAt) : ''}
                  placeholder="Unesite datum ili koristite kontakt istoriju"
                  readOnly
                />
              </div>
            </div>
            <div className="col-12">
              <div className="field">
                <label>Napomene</label>
                <InputTextarea value={supplierForm.notes || ''} onChange={(e) => setSupplierForm((p) => ({ ...p, notes: e.target.value }))} rows={4} />
              </div>
            </div>
          </div>

          <div className="mt-3">
            <div style={{ fontWeight: 700, marginBottom: 8 }}>Istorija cena (12 meseci unazad)</div>
            <div className="grid">
              {last12Months.map((m) => (
                <div key={m.key} className="col-12 md:col-4">
                  <div className="field">
                    <label>{m.label}</label>
                    <InputNumber
                      value={supplierPrices[m.key] ?? null}
                      onValueChange={(e) => setSupplierPrices((p) => ({ ...p, [m.key]: e.value ?? null }))}
                      min={0}
                      mode="decimal"
                      minFractionDigits={0}
                      maxFractionDigits={2}
                      placeholder="Cena"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default LogisticsGoods;


